import React, { useState, useEffect, useCallback } from 'react';
import './SystemStatusIndicator.css';

const SERVICE_LABELS = {
  frontend: 'Frontend',
  backend: 'Backend',
  database: 'Database',
  api: 'API'
};

const STATUS_COLORS = {
  error: '#ef4444',
  warning: '#f97316',
  ok: '#10b981',
  unknown: '#9ca3af'
};

const STATUS_ICONS = {
  error: '❌',
  warning: '⚠️',
  ok: '✅',
  unknown: 'ℹ️'
};

const STATUS_PRIORITIES = {
  error: 3,
  warning: 2,
  ok: 1,
  unknown: 0
};

const formatRelativeDate = (value) => {
  if (!value) return 'Ikke registreret';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ukendt';
  const deltaMs = Date.now() - date.getTime();
  if (deltaMs < 60 * 1000) return 'lige nu';
  if (deltaMs < 60 * 60 * 1000) {
    const mins = Math.round(deltaMs / (60 * 1000));
    return `${mins} min siden`;
  }
  const hours = Math.round(deltaMs / (60 * 60 * 1000));
  return `${hours} timer siden`;
};

const formatTimestamp = (value) => {
  if (!value) return 'Uden tid';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ukendt';
  return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
};

const formatFreshness = (freshness) => {
  if (!freshness?.state) return 'UKENDT';
  if (freshness.state === 'fresh') return 'FRESH';
  if (freshness.state === 'aging') return 'ALDRER';
  if (freshness.state === 'stale') return 'STALE';
  return 'UKENDT';
};

const getWorstService = (services = {}) => {
  const entries = Object.entries(services);
  if (!entries.length) {
    return { key: null, status: 'unknown' };
  }

  entries.sort(([, aStatus], [, bStatus]) => {
    const aValue = STATUS_PRIORITIES[aStatus] ?? 0;
    const bValue = STATUS_PRIORITIES[bStatus] ?? 0;
    return bValue - aValue;
  });

  const [key, status] = entries[0];
  return { key, status };
};

const buildSummaryLine = (services = {}, fallback = 'System status ukendt') => {
  const entries = Object.entries(services);
  if (!entries.length) {
    return fallback;
  }

  return entries
    .slice(0, 3)
    .map(([key, value]) => `${SERVICE_LABELS[key] || key} ${value}`)
    .join(' · ');
};

const SystemStatusIndicator = ({ apiBaseUrl = '' }) => {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/health/quick`);
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setStatus({
        status: 'error',
        services: {
          frontend: 'unknown',
          backend: 'error',
          database: 'error',
          api: 'error'
        },
        timestamp: new Date().toISOString(),
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const aggregate = getWorstService(status?.services);
  const buttonColor = STATUS_COLORS[aggregate.status] || STATUS_COLORS.unknown;
  const buttonTitle = aggregate.key
    ? `${SERVICE_LABELS[aggregate.key] || aggregate.key} ${aggregate.status}`
    : 'System status';

  return (
    <div className="system-status-container">
      <button
        type="button"
        className="system-status-button"
        style={{ borderColor: buttonColor }}
        onClick={() => setExpanded(prev => !prev)}
        title={buttonTitle}
        aria-label={loading ? 'Kontrollerer systemstatus' : buttonTitle}
        aria-expanded={expanded}
      >
        <span className="status-dot" style={{ backgroundColor: buttonColor }} />
        <div className="status-summary">
          <span className="status-button-title">
            {loading ? 'Checker systemet...' : buttonTitle}
          </span>
          <span className="status-button-subtitle">
            {loading ? 'Opdaterer status' : buildSummaryLine(status?.services)}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="status-dropdown">
          <div className="status-header">
            <div>
              <strong>System Status</strong>
              <div className="status-timestamp">
                {status?.timestamp ? `Sidst tjekket kl. ${formatTimestamp(status.timestamp)}` : 'Tidspunkt ukendt'}
              </div>
            </div>
            <button type="button" className="close-btn" onClick={() => setExpanded(false)} aria-label="Luk systemstatus">×</button>
          </div>

          <div className="status-items">
            {(status?.services ? Object.entries(status.services) : []).map(([key, value]) => (
              <div className="status-row" key={key}>
                <span className="status-chip" style={{ backgroundColor: STATUS_COLORS[value] || STATUS_COLORS.unknown }}>
                  {STATUS_ICONS[value] || 'ℹ️'}
                </span>
                <div className="status-content">
                  <span className="status-label">{SERVICE_LABELS[key] || key}</span>
                  <span className="status-value">{value.toUpperCase()}</span>
                </div>
                <span className="status-detail">{loading ? '...' : ''}</span>
              </div>
            ))}

            {status?.lastSyncAt && (
              <div className="status-row info-row">
                <span className="status-chip info-chip">⏱️</span>
                <div className="status-content">
                  <span className="status-label">Sidste sync</span>
                  <span className="status-value">{formatTimestamp(status.lastSyncAt)}</span>
                </div>
                <span className="status-detail">{formatRelativeDate(status.lastSyncAt)}</span>
              </div>
            )}

            {(status?.priceListLastSyncedAt || status?.priceListFreshness) && (
              <div className="status-row info-row">
                <span className="status-chip info-chip">🧾</span>
                <div className="status-content">
                  <span className="status-label">Sidste prislistesync</span>
                  <span className="status-value">
                    {formatTimestamp(status.priceListLastSyncedAt)} · {formatFreshness(status.priceListFreshness)}
                  </span>
                </div>
                <span className="status-detail">{formatRelativeDate(status.priceListLastSyncedAt)}</span>
              </div>
            )}

            {status?.error && (
              <div className="status-row info-row">
                <span className="status-chip info-chip">⚠️</span>
                <div className="status-content">
                  <span className="status-label">Fejl</span>
                  <span className="status-value">{status.error}</span>
                </div>
              </div>
            )}
          </div>

          <div className="status-footer">
            <button type="button" className="refresh-btn" onClick={fetchStatus} disabled={loading}>
              🔄 Opdater
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatusIndicator;
